import {useGridAccess} from "../../grid/access";
import React, {useEffect, useRef, useState} from "react";
import {Link, useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {useInfiniteQuery, useQueryClient} from "@tanstack/react-query";
import Button from "@rescui/button";
import {Input} from "@rescui/input";
import {useTextStyles} from "@rescui/typography";
import {useAuth} from "../../hooks/useAuth";
import {GridAccountRoot} from "../../grid/ui/GridAccountRoot";
import {GridDialog} from "../../grid/ui/GridDialog";
import {listGridProjects, ProjectRequestError} from "../../grid/projects";
import {ProjectActionDialog, type ProjectAction} from "./ProjectActionDialog";
import "./index.css";

export function ProjectsLink({className}: {className?: string}) {
  const location = useLocation(), params = new URLSearchParams(location.search);
  params.set("projects", "true");
  return <Link to={{pathname: location.pathname, search: `?${params}`, hash: location.hash}} className={className} aria-haspopup="dialog">My projects</Link>;
}

export function ProjectsOverlay() {
  const [params, setParams] = useSearchParams();
  const {user} = useAuth(), access = useGridAccess();
  if (params.get("projects") !== "true" || !user || access === "disabled") return null;
  return <ProjectsWorkspace email={user.email} onClose={() => setParams((current) => {
    const next = new URLSearchParams(current);
    for (const key of ["projects", "projectQuery", "projectTrash"]) next.delete(key);
    return next;
  }, {replace: true})} />;
}

function ProjectsWorkspace({email, onClose}: {email: string; onClose: () => void}) {
  const readOnly = useGridAccess() === "read-only";
  const location = useLocation(), embedded = location.pathname !== "/projects";
  const queryKey = embedded ? "projectQuery" : "q", trashKey = embedded ? "projectTrash" : "trash";
  const textCn = useTextStyles(), [params, setParams] = useSearchParams();
  const q = params.get(queryKey) || "", trash = params.get(trashKey) === "true", client = useQueryClient();
  const [action, setAction] = useState<ProjectAction | null>(null);
  const [search, setSearch] = useState(q);
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => setSearch(q), [q]);
  const query = useInfiniteQuery({queryKey: ["grid-projects", email, q, trash], initialPageParam: null as string | null,
    queryFn: ({pageParam, signal}) => listGridProjects(q, pageParam, signal, trash), getNextPageParam: (page) => page.nextCursor,
    staleTime: 0, refetchOnMount: "always"});
  const projects = query.data ? query.data.pages.flatMap((page) => page.projects) : [];
  const refresh = () => client.invalidateQueries({queryKey: ["grid-projects", email]});
  const viewParams = (value: string, inTrash: boolean) => {
    const next = new URLSearchParams(params);
    next.delete(queryKey); next.delete(trashKey);
    if (value) next.set(queryKey, value);
    if (inTrash) next.set(trashKey, "true");
    return next;
  };
  return <><GridDialog title="My projects" className="projects-dialog" initialFocusRef={searchRef} onClose={onClose}>
    <Button className="projects-close" size="s" mode="clear" aria-label="Close My projects" onClick={onClose}>×</Button>
    <div className="projects-controls">
      <p className="projects-note projects-account">{email} · Private Grids</p>
      {readOnly && <p role="status">Grid is temporarily read-only. Saved projects remain available.</p>}
      <div className="projects-toolbar">
        <nav className="projects-views" aria-label="Project views"><Link to={{search: `?${viewParams("", false)}`}} aria-current={!trash ? "page" : undefined}>My projects</Link>
          <Link to={{search: `?${viewParams("", true)}`}} aria-current={trash ? "page" : undefined}>Trash</Link></nav>
        {!readOnly && <div className="projects-tools">
          {!trash && <Button size="s" mode="outline" onClick={() => setAction({action: "import"})}>Import snapshot</Button>}
          {location.pathname !== "/grid" && <Link to="/grid" className={textCn("rs-link")}>+ New grid</Link>}
        </div>}
      </div>
      <form className="projects-search" onSubmit={(event) => {event.preventDefault(); setParams(viewParams(search.trim(), trash));}}>
        <Input ref={searchRef} id="project-search" aria-label="Search projects" placeholder="Search projects" maxLength={100} value={search} onChange={(event) => setSearch(event.target.value)} />
        <Button size="s" mode="outline" type="submit">Search</Button>
        {q && <Button size="s" mode="clear" type="button" onClick={() => {setSearch(""); setParams(viewParams("", trash));}}>Clear search</Button>}
      </form>
    </div>
    <div className="projects-list">
      <p className="projects-note projects-list-caption">{q ? `Results for “${q}”` : trash ? "Restore a project to edit it again. Files and shared snapshots are preserved." : "Your saved Grids, most recently updated first."}</p>
      {query.isPending && <p role="status">Loading projects…</p>}
      {query.isError && <div className="projects-message"><p role="alert">{query.error.message}</p>
        {query.error instanceof ProjectRequestError && query.error.status === 401
          ? <a className={textCn("rs-link")} href={`/auth/login?next=${encodeURIComponent(location.pathname + location.search)}`}>Sign in again</a>
          : <Button size="s" mode="outline" onClick={() => void (query.isFetchNextPageError ? query.fetchNextPage() : query.refetch())}>Retry</Button>}
      </div>}
      {!query.isPending && !query.isError && projects.length === 0 && <div className="projects-message">
        <h2>{q ? "No matching projects" : trash ? "Trash is empty" : "No saved projects yet"}</h2>
        <p className="projects-note">{q ? "Try another name or clear the search." : trash ? "Projects you move to Trash will appear here." : "Create a Grid and press Save to find it here next time."}</p>
        {!q && !trash && !readOnly && <Link to="/grid" className={textCn("rs-link")}>Create a grid</Link>}
      </div>}
      <div className="projects-cards">{projects.map((project) => <article key={project.projectId} className="projects-card">
        <div className="projects-card-info">
        <h2>{trash ? project.title || "Untitled grid" : <Link to={project.editPath} className={textCn("rs-link")}>{project.title || "Untitled grid"}</Link>}</h2>
        <p className="projects-note">{project.bannerCount} {project.bannerCount === 1 ? "banner" : "banners"} · Saved <time dateTime={project.updatedAt}>{new Date(project.updatedAt).toLocaleString()}</time></p>
        </div>
        <div className="projects-card-actions">{!readOnly && (trash ? <Button size="s" mode="outline" onClick={() => setAction({action: "restore", project})}>Restore</Button> : <>
          <Button size="s" mode="clear" onClick={() => setAction({action: "rename", project})}>Rename</Button>
          <Button size="s" mode="clear" onClick={() => setAction({action: "duplicate", project})}>Duplicate</Button>
          <Button size="s" mode="clear" onClick={() => setAction({action: "trash", project})}>Move to Trash</Button></>)}</div>
      </article>)}</div>
      {query.hasNextPage && <Button size="s" mode="outline" disabled={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>{query.isFetchingNextPage ? "Loading…" : "Load more projects"}</Button>}
    </div>
  </GridDialog>{action && <ProjectActionDialog selection={action} onClose={() => {setAction(null); void refresh();}} onComplete={async () => {await refresh(); setAction(null);}} />}</>;
}
function ProjectsEntry({email}: {email: string}) {
  const navigate = useNavigate();
  return <ProjectsWorkspace email={email} onClose={() => navigate("/grid", {replace: true})} />;
}
export default function ProjectsRoot() {
  return <GridAccountRoot>{(_role, email) => <ProjectsEntry email={email} />}</GridAccountRoot>;
}
